/**
 * Task #704 — Manager Panel.
 *
 * Mirror of the Admin Panel layout (left-nav sections + content area),
 * gated on `isManager=true`. Sections:
 *   - My Team
 *   - Team Goals & Quotas
 *   - Talent Settings (only when the manager's department is Talent)
 *   - Team Templates
 *   - Team Automations
 *   - Approvals
 *   - Notifications
 *
 * Implementation note: sections that reuse Admin components pass an
 * `inPanel` prop where supported. Sections without a ready-made
 * Admin component render a scoped read-only view that points the
 * manager at the equivalent settings surface.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  Users, Target, Star, FileText, Zap, ClipboardCheck, Bell, Loader2, UserPlus,
} from "lucide-react";
import {
  ApprovalOverridesPanel,
  ApprovalsInbox,
} from "@/components/approvals/approvals-shared";
import { ManageTalentDialog } from "@/components/manage-talent-dialog";
import { ManagerGamificationTile } from "@/components/gamification/manager-tile";
import { FormsTemplatesDialog } from "@/components/forms-templates-dialog";
import NotificationSettingsPage from "@/pages/notification-settings";
import NotFound from "@/pages/not-found";
import { Badge } from "@/components/ui/badge";
import type { UserProfile, EmailAutomation, ManagerSeatRequest } from "@shared/schema";

interface MeResponse {
  isManager: boolean;
  canSeeTeam: boolean;
}

interface TeamMember {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role?: string;
  departmentId?: string;
}

const SECTIONS = [
  { key: "team", label: "My Team", icon: Users, description: "Direct + indirect reports in your reporting chain." },
  { key: "goals", label: "Team Goals & Quotas", icon: Target, description: "Targets and quotas for your team." },
  { key: "talent", label: "Talent Settings", icon: Star, description: "Show offerings and booking forms for talent owned by your team.", deptOnly: "talent" as const },
  { key: "templates", label: "Team Templates", icon: FileText, description: "Email, SMS, and document templates scoped to your team." },
  { key: "automations", label: "Team Automations", icon: Zap, description: "Drip campaigns and automations for leads/clients owned by your team." },
  { key: "approvals", label: "Approvals", icon: ClipboardCheck, description: "Approval thresholds for your team and your inbox." },
  { key: "notifications", label: "Notifications", icon: Bell, description: "Team-level notification defaults." },
];

export default function ManagerPanelPage() {
  const [, navigate] = useLocation();
  const [selected, setSelected] = useState("team");

  const { data: profile, isLoading: profileLoading } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });
  const { data: me, isLoading: meLoading } = useQuery<MeResponse>({
    queryKey: ["/api/manager-dashboard/me"],
  });

  // Deep-link `?section=approvals`
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get("section");
    if (s && SECTIONS.some((x) => x.key === s)) {
      setSelected(s);
      params.delete("section");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const isManagerOrAdmin = me?.canSeeTeam === true
    || (profile?.role || "").toLowerCase() === "admin";
  const departmentId = (profile as any)?.departmentId as string | undefined;

  const sections = useMemo(() => {
    return SECTIONS.filter((s) => !s.deptOnly || s.deptOnly === departmentId
      || (profile?.role || "").toLowerCase() === "admin");
  }, [departmentId, profile?.role]);

  if (profileLoading || meLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isManagerOrAdmin) {
    // Individual contributors should not even see this surface exists —
    // render the standard 404 so the route is indistinguishable from
    // an unmapped URL.
    return <NotFound />;
  }

  const currentSection = sections.find((s) => s.key === selected) || sections[0];

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="w-56 border-r bg-muted/30 flex flex-col shrink-0">
        <div className="px-4 py-4 border-b">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Manager Panel</h2>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-0.5">
            {sections.map((s) => {
              const Icon = s.icon;
              const active = selected === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setSelected(s.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left transition-colors ${
                    active ? "bg-primary text-primary-foreground" : "hover-elevate text-foreground"
                  }`}
                  data-testid={`button-mp-menu-${s.key}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{s.label}</span>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b px-3 sm:px-6 py-3 sm:py-4 shrink-0">
          <h1 className="text-xl font-semibold" data-testid="text-page-title">
            {currentSection?.label || "Manager Panel"}
          </h1>
          <p className="text-sm text-muted-foreground">{currentSection?.description}</p>
        </div>

        <div className="flex-1 overflow-auto">
          <SectionRouter selected={selected} />
        </div>
      </div>
    </div>
  );
}

function SectionRouter({ selected }: { selected: string }) {
  switch (selected) {
    case "team":
      return <MyTeamSection />;
    case "goals":
      // Team-scoped view only. The workspace-wide GamificationSettingsDialog
      // belongs to admins and is intentionally not embedded here — managers
      // see leaderboard + progress for their direct/indirect reports via
      // ManagerGamificationTile, which is already scoped server-side.
      return (
        <div className="p-4 sm:p-6 space-y-6">
          <ManagerGamificationTile />
        </div>
      );
    case "talent":
      // Talent-department managers only (filtered by deptOnly above). The
      // ManageTalentDialog reads /api/talent which is workspace-scoped on
      // the server; admin-only settings inside the dialog are gated by
      // the same role checks the dialog already enforces.
      return <ManageTalentDialog open={true} onOpenChange={() => {}} inPanel={true} />;
    case "templates":
      // Reuse the workspace Templates surface; admin-only template
      // categories are filtered inside FormsTemplatesDialog by role.
      // Managers create/edit team templates; workspace templates surface
      // read-only.
      return (
        <div className="p-4 sm:p-6 space-y-4">
          <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground" data-testid="text-templates-scope-note">
            Workspace-level templates appear read-only here. Templates you create from this panel are owned by you and visible to your team.
          </div>
          <FormsTemplatesDialog open={true} onOpenChange={() => {}} inPanel={true} />
        </div>
      );
    case "automations":
      return <ManagerAutomationsSection />;
    case "approvals":
      return (
        <div className="space-y-6">
          <ApprovalOverridesPanel />
          <div className="px-4 sm:px-6">
            <h2 className="text-sm font-semibold mb-2">Approvals inbox</h2>
          </div>
          <ApprovalsInbox scope="manager" />
        </div>
      );
    case "notifications":
      // Reuse the per-user notification preferences UI directly in-panel.
      // This is the same surface available at /notification-settings and
      // edits the caller's own preferences (read-only fallback to
      // workspace defaults shown inline).
      return (
        <div className="p-0">
          <NotificationSettingsPage />
        </div>
      );
    default:
      return null;
  }
}

function MyTeamSection() {
  const { toast } = useToast();
  const { user } = useAuth();
  const callerId = (user as any)?.id as string | undefined;
  const { data, isLoading } = useQuery<{ team: TeamMember[] }>({
    queryKey: ["/api/manager-dashboard/team"],
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("sales_rep");
  const [inviteReason, setInviteReason] = useState("");

  const team = data?.team || [];

  const reassignMutation = useMutation({
    mutationFn: async ({ userId, reportsToUserId }: { userId: string; reportsToUserId: string | null }) => {
      return apiRequest("PATCH", `/api/manager/team/${userId}/reports-to`, { reportsToUserId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/manager-dashboard/team"] });
      toast({ title: "Report reassigned" });
    },
    onError: (e: any) =>
      toast({ title: "Couldn't reassign", description: e.message, variant: "destructive" }),
  });

  const inviteMutation = useMutation({
    mutationFn: async (body: { email: string; role: string; reason: string }) => {
      return apiRequest("POST", "/api/manager/team/seat-requests", body);
    },
    onSuccess: () => {
      toast({
        title: "Seat request sent",
        description: "Your admin has been notified and will send the invite.",
      });
      setInviteOpen(false);
      setInviteEmail("");
      setInviteReason("");
    },
    onError: (e: any) =>
      toast({ title: "Couldn't send request", description: e.message, variant: "destructive" }),
  });

  if (isLoading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  // Candidates for "reports to": the manager's other reports (any teammate in
  // the subtree). Backend additionally accepts the caller themselves.
  const reassignCandidates = team;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Your reports</CardTitle>
            <CardDescription>
              Everyone in your direct + indirect reporting chain. Reassign anyone to another
              manager inside your tree, or request a new seat — your admin will be notified to
              send the invite.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setInviteOpen(true)} data-testid="button-invite-teammate">
            <UserPlus className="w-4 h-4 mr-1.5" />
            Request seat
          </Button>
        </CardHeader>
        <CardContent>
          {team.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reports yet.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {team.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                  data-testid={`team-member-${m.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {[m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || m.id}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {m.email || "—"}{m.role ? ` · ${m.role}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select
                      onValueChange={(val) => {
                        // "__me__" means "report directly to me" — map to
                        // the caller's user id, not null (null would clear
                        // the report's manager entirely).
                        const target = val === "__me__" ? callerId ?? null : val;
                        if (!target) {
                          toast({
                            title: "Couldn't reassign",
                            description: "Missing caller id; please refresh.",
                            variant: "destructive",
                          });
                          return;
                        }
                        reassignMutation.mutate({ userId: m.id, reportsToUserId: target });
                      }}
                      disabled={reassignMutation.isPending}
                    >
                      <SelectTrigger
                        className="w-[180px] h-8 text-xs"
                        data-testid={`select-reports-to-${m.id}`}
                      >
                        <SelectValue placeholder="Reassign to…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__me__">Me (direct report)</SelectItem>
                        {reassignCandidates
                          .filter((c) => c.id !== m.id)
                          .map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {[c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || c.id}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <SeatRequestsHistory />

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a new teammate</DialogTitle>
            <DialogDescription>
              Your admin handles the actual invite. We'll notify them with the details below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@example.com"
                data-testid="input-invite-email"
              />
            </div>
            <div>
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger id="invite-role" data-testid="select-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales_rep">Sales rep</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="talent">Talent</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="invite-reason">Reason (optional)</Label>
              <Input
                id="invite-reason"
                value={inviteReason}
                onChange={(e) => setInviteReason(e.target.value)}
                placeholder="Why this seat is needed"
                data-testid="input-invite-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button
              onClick={() =>
                inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole, reason: inviteReason.trim() })
              }
              disabled={inviteMutation.isPending || !inviteEmail.trim()}
              data-testid="button-submit-seat-request"
            >
              {inviteMutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManagerAutomationsSection() {
  const { data, isLoading } = useQuery<EmailAutomation[]>({
    queryKey: ["/api/email-automations"],
  });

  if (isLoading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const list = data || [];
  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Team Automations</CardTitle>
          <CardDescription>
            Drip campaigns and automations visible to your team. Workspace-wide automations are
            read-only here — manage your own from the Email Hub.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {list.length === 0 ? (
            <p className="text-sm text-muted-foreground">No automations configured yet.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {list.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                  data-testid={`automation-${a.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{a.name || "(untitled)"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {a.triggerType || "—"}{a.description ? ` · ${a.description}` : ""}
                    </p>
                  </div>
                  <Badge variant={a.isActive ? "default" : "secondary"} className="shrink-0">
                    {a.isActive ? "Active" : "Paused"}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SeatRequestsHistory() {
  const { data, isLoading } = useQuery<{ requests: ManagerSeatRequest[] }>({
    queryKey: ["/api/manager/team/seat-requests"],
  });
  if (isLoading || !data || data.requests.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Your seat requests</CardTitle>
        <CardDescription>
          Awaiting admin approval before the invite is sent.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y rounded-md border">
          {data.requests.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-3 px-3 py-2"
              data-testid={`seat-request-${r.id}`}
            >
              <div className="min-w-0">
                <p className="font-medium truncate">{r.email}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {r.role}{r.reason ? ` · ${r.reason}` : ""}
                </p>
              </div>
              <Badge
                variant={
                  r.status === "approved"
                    ? "default"
                    : r.status === "rejected"
                    ? "destructive"
                    : "secondary"
                }
                className="shrink-0"
              >
                {r.status}
              </Badge>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function PlaceholderSection({ title, body, ctaHref, ctaLabel }: {
  title: string; body: string; ctaHref: string; ctaLabel: string;
}) {
  const [, navigate] = useLocation();
  return (
    <div className="p-4 sm:p-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => navigate(ctaHref)} data-testid="button-section-cta">
            {ctaLabel}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
